import SimpleGrid from "@ui-kit/layout/Grid/SimpleGrid.web";
import { useMobileWebviewContext } from "@shared/contexts/MobileWebviewContext/MobileWebviewContext";
import { useEmbeddedComponent } from "@shared/hooks/useEmbeddedComponent";
import useRedirect from "@shared/hooks/useRedirect.web";
import EventBadges from "@shared/modules/events/components/event-details/EventBadges/EventBadges.web";
import EventRestrictions from "@shared/modules/events/components/EventRestrictions/EventRestrictions.web";
import MembershipBadges from "@shared/modules/memberships/components/MembershipDetailsHeader/badges/MembershipBadges";
import { useTranslation } from "@shared/utils/internationalization";
import { isStringWithContent } from "@shared/utils/text/utils";
import url from "@shared/utils/urls/url";
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Button from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import ParsedHtml from "@ui-kit/typography/ParsedHtml/ParsedHtml.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useRouter } from "next/router";
import { SeatingInfoModalProps } from "./SeatingInfoModal.props";

/*
 * Contains the Seating Info modal, Help & Info modal, and Contact Details modal
 * Controls the opening/closing of the main info modal when another modal is selected
 */
const SeatingInfoModal = ({
  isOpen,
  onClose,
  seatingObject,
  venueObject,
  onHelpAndInfoOpen,
  onContactDetailsOpen,
}: SeatingInfoModalProps) => {
  const { t } = useTranslation("purchases");

  const { redirect } = useRedirect();
  const { webviewValueAdapter } = useMobileWebviewContext();
  const { isEmbedded } = useEmbeddedComponent();
  const router = useRouter();

  const refundPolicyText =
    seatingObject?.refund_policy ||
    venueObject?.default_refund_policy ||
    t("This venue hasn't specified a refund policy.");

  const isMembershipSeatingPage =
    router.pathname === "/m/[membershipSlug]/seating" ||
    router.pathname === "/pass/[membershipSlug]/seating";

  const getBadgeElement = () => {
    if (isMembershipSeatingPage) {
      return MembershipBadges({
        isSmallerThanSm: false,
        membershipGroupData: seatingObject,
      });
    }
    return EventBadges({
      isSmallerThanSm: false,
      eventData: seatingObject,
      eventTabOptions: null,
      selectedTab: null,
      setSelectedTab: null,
      handleViewMap: null,
      isAttractionPage: false,
    });
  };

  const contactButtonInfo = [
    {
      id: 0,
      icon: { name: "User", ariaLabel: t("profile icon") },
      name: t("Organizer Profile"),
      handleClick: () => {
        // open the profile link in a new tab if on the seating widget
        if (isEmbedded) {
          window.open(url("o/{slug}", { slug: venueObject.slug }), "_blank");
        } else {
          // TODO: remove asWindow when profile page moved to NextJS
          redirect(url("o/{slug}", { slug: venueObject.slug }), {
            asWindow: true,
          });
        }
      },
      showButton: webviewValueAdapter({ web: true, webview: false }),
    },
    {
      id: 1,
      icon: { name: "DangerCircle", ariaLabel: t("help icon") },
      name: t("Help & Info"),
      handleClick: onHelpAndInfoOpen,
      showButton: true,
    },
    {
      id: 2,
      icon: { name: "Call", ariaLabel: t("contact icon") },
      name: t("Contact Us"),
      handleClick: onContactDetailsOpen,
      showButton: !venueObject.is_test,
    },
  ];

  const visibleButtons = contactButtonInfo.filter(
    (filterButton) => filterButton.showButton
  );

  const showEventDescription = Boolean(
    (seatingObject?.description &&
      isStringWithContent(seatingObject?.description)) ||
      (seatingObject?.restrictions && seatingObject?.restrictions.length > 0)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
      isCentered
    >
      <Modal.Header>{t("More Info")}</Modal.Header>
      <Modal.Body>
        <Flex direction="column">
          <Text fontSize="xl" fontWeight="bold">
            {seatingObject?.name}
          </Text>
          <Text fontSize="lg" color="gray.600">
            {seatingObject?.subtitle}
          </Text>
          {/* Shows start/end time, plus location */}
          <Flex>
            <SimpleGrid
              w="full"
              mt="xs"
              fontSize="sm"
              columns={{ base: 1, sm: 2 }}
              hidden={!getBadgeElement().find((badge) => badge.showBadge)}
            >
              {getBadgeElement().map(
                ({ id, showBadge, icon, text }) =>
                  showBadge && (
                    <Flex
                      key={id}
                      alignItems={{ base: "center", sm: "flex-start" }}
                      mr="lg"
                      p="8px 0px"
                    >
                      {icon} {text}
                    </Flex>
                  )
              )}
            </SimpleGrid>
          </Flex>
          <Divider my="xl" />
          {/* Shows the event's description */}
          {showEventDescription && (
            <>
              <Text
                mb="xs"
                fontSize={{ base: "md", sm: "lg" }}
                fontWeight="bold"
                color="primary.600"
              >
                {t("Description")}
              </Text>
              {seatingObject?.restrictions && (
                <Flex mb="xs">
                  <EventRestrictions
                    restrictionArray={seatingObject?.restrictions}
                  />
                </Flex>
              )}
              {seatingObject?.description && (
                <ParsedHtml htmlString={seatingObject.description} />
              )}
              <Divider my="xl" />
            </>
          )}
          {/* Shows the organizer's contact info */}
          <Text
            mb="xs"
            fontSize={{ base: "md", sm: "lg" }}
            fontWeight="bold"
            color="primary.600"
          >
            {t("Contact Information")}
          </Text>
          {venueObject?.description && (
            <Text mb="md" fontSize={{ base: "0.75rem", sm: "0.875rem" }}>
              {venueObject.description}
            </Text>
          )}
          <Flex
            direction={{ base: "column", lg: "row" }}
            justifyContent="space-between"
            gap="md"
            flexWrap="wrap"
            w="full"
          >
            {visibleButtons?.map((button) => (
              <Button
                data-testid={`seating-info-modal-${button?.name}`}
                key={button.id}
                cursor="pointer"
                display="flex"
                flexDirection="column"
                alignItems={{ base: "flex-start", lg: "center" }}
                w={{ base: "full", lg: "auto" }}
                flex={{ lg: "1" }}
                h="full"
                p="md"
                fontSize="sm"
                color="primary.600"
                backgroundColor="gray.200"
                borderRadius="md"
                fontWeight="default"
                _hover={{
                  _light: { bg: "gray.400" },
                  _dark: { bg: "whiteAlpha.400" },
                }}
                onClick={() => button.handleClick()}
              >
                <Flex
                  direction={{ base: "row", lg: "column" }}
                  alignItems="center"
                >
                  <Icon
                    name={button.icon.name}
                    aria-label={button.icon.ariaLabel}
                    boxSize="element.2xl"
                    mr={{ base: "sm", lg: "none" }}
                  />
                  <Text wordBreak="break-word" pt="2xs">
                    {button?.name}
                  </Text>
                </Flex>
              </Button>
            ))}
          </Flex>
          <Divider my="xl" />
          {/* Shows the event or venue's refund policy */}
          <Text
            mb="xs"
            fontSize={{ base: "md", sm: "lg" }}
            fontWeight="bold"
            color="primary.600"
          >
            {t("Refund Policy")}
          </Text>
          <Text fontSize={{ base: "0.75rem", sm: "0.875rem" }}>
            {refundPolicyText}
          </Text>
        </Flex>
      </Modal.Body>
    </Modal>
  );
};

export default SeatingInfoModal;
